import type { BottomSheetModal } from '@gorhom/bottom-sheet';
import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useEffect, useRef } from 'react';
import { BackHandler } from 'react-native';
import { KeyboardController } from 'react-native-keyboard-controller';

type UseBottomModalPresentationArgs = {
  isVisible: boolean;
  onClose?: () => void;
};

/**
 * Lifecycle of BottomModal: presents/dismisses the sheet edge-triggered
 * (`isVisible` transitions only), syncs the keyboard down on close and
 * intercepts the hardware back button while the sheet is visible.
 */
export const useBottomModalPresentation = ({
  isVisible,
  onClose
}: UseBottomModalPresentationArgs) => {
  const bottomSheetModalRef = useRef<BottomSheetModal>(null);
  const wasVisibleRef = useRef(false);
  const hasRequestedCloseRef = useRef(false);

  const requestClose = useCallback(() => {
    if (hasRequestedCloseRef.current) return;

    hasRequestedCloseRef.current = true;
    onClose?.();
  }, [onClose]);

  const handleClose = useCallback(() => {
    KeyboardController.dismiss();
    requestClose();
  }, [requestClose]);

  useEffect(() => {
    // Edge-triggered: only present() on false->true and dismiss() on true->false.
    // A gesture/backdrop dismiss already unmounts the sheet and fires onDismiss
    // (which sets isVisible=false); firing dismiss() again on the already
    // dismissed modal corrupts @gorhom v5's internal mount/status state and
    // makes the next present() a no-op. Guarding on the edge prevents that.
    if (isVisible === wasVisibleRef.current) {
      return;
    }
    wasVisibleRef.current = isVisible;

    KeyboardController.dismiss();
    if (isVisible) {
      hasRequestedCloseRef.current = false;
      bottomSheetModalRef.current?.present();
    } else {
      bottomSheetModalRef.current?.dismiss();
    }
  }, [isVisible]);

  const handleDismiss = useCallback(() => {
    const shouldRequestClose = wasVisibleRef.current;
    // The sheet is already unmounted here (gesture/backdrop/programmatic).
    // Sync the edge ref so the follow-up isVisible=false from the parent does
    // not trigger a redundant dismiss() on the dismissed modal.
    wasVisibleRef.current = false;
    KeyboardController.dismiss();
    if (shouldRequestClose) requestClose();
  }, [requestClose]);

  useFocusEffect(
    useCallback(() => {
      if (!isVisible) return;
      const subscription = BackHandler.addEventListener(
        'hardwareBackPress',
        () => {
          handleClose();
          return true;
        }
      );

      return () => subscription.remove();
    }, [handleClose, isVisible])
  );

  return { bottomSheetModalRef, handleClose, handleDismiss };
};
